// masterOrganization — "does this thing belong to the LiftNet MASTER
// organization?" in one place.
//
// Roles owned by the master org are LiftNet-level templates shared with every
// tenant: GET /roles/ unions the master org's own roles into every org's list,
// so they surface in tables and pickers that belong to other organizations.
// The UI marks them (see MasterRoleStar) and, in several places, refuses to
// edit them — every one of those checks was previously an inline
// `orgId === process.env.REACT_APP_MASTER_ORG_ID` copy in each component.
//
// This is deliberately NOT in isMasterAdmin.ts: that answers "is the logged-in
// USER a master admin", which additionally resolves through the role NAME so it
// keeps working on offline installs where the master org's UUID is generated at
// runtime. Ownership of a record is a plain id comparison and has no such
// fallback — a record's org id is the only thing that identifies it.

import { formatUuidT } from "../flatbuffers/display";

/**
 * True when the given organization id is the master organization (LiftNet).
 * Accepts a plain UUID string; false for an empty id or an unconfigured
 * REACT_APP_MASTER_ORG_ID, so a missing env var never marks everything master.
 */
export function isMasterOrgId(orgId: string): boolean {
  const masterOrgId = process.env.REACT_APP_MASTER_ORG_ID;
  return !!masterOrgId && !!orgId && orgId === masterOrgId;
}

/**
 * True when a role record belongs to the master organization.
 *
 * Reads the org id off whichever shape the record arrived in — REST JSON
 * (`org_id`), FlatBuffer-unpacked (`orgId`, possibly a UuidT object) — and
 * normalizes it through formatUuidT before comparing.
 */
export function isMasterOrgRole(role: any): boolean {
  return isMasterOrgId(formatUuidT(role?.org_id ?? role?.orgId));
}

export default isMasterOrgId;
